<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwSurface from '@/components/lib/base/GwSurface/GwSurface.vue'
import { t } from '@/locales/i18n'
import ActionSequenceLine from '@/components/lib/GwTable/ActionSequence/ActionSequenceLine.vue'
import { Portal } from 'portal-vue'
import {
  displayAsRaise,
  betActions,
  mainFirstActions,
  mainNextActions,
  type ActionSequence,
  type BetAction,
  type MainAction,
  type SelectedActions,
} from '@/components/lib/GwTable/ActionSequence/actionSequenceUtils'
import * as R from 'remeda'
import type { Street } from '@/types/api/reports'
import { CALL_DEBOUNCE_MS } from '@/helpers/constantsNew'

interface ActionsSequenceFilterProps {
  actionsSequence: ActionSequence
  street: Street
  variant: 'split' | 'single'
}

const props = withDefaults(defineProps<ActionsSequenceFilterProps>(), {
  variant: 'split',
})

const emit = defineEmits<{
  'update:value': [ActionSequence]
}>()

const getNextActions = (prevActions: SelectedActions): MainAction[] => {
  const prevAction = R.last(prevActions)

  if (!prevAction) return mainNextActions // First action of the sequence
  if (prevAction === 'CHECK') return mainFirstActions // No FOLD or CALL after CHECK
  if (prevAction === 'ANY')
    // Hard to determine – need to check all possible actions.
    return R.pipe(
      prevActions,
      R.dropLast(1), // Remove the last element (i.e. 'ANY')
      getNextActions, // Recurse with the shortened sequence
      R.filter((a: MainAction) => a !== 'ANY'), // Remove any remaining 'ANY'
      R.flatMap((a: MainAction) => getNextActions([a])), // Map each action to its possible next actions and flatten the result
      R.unique(), // Remove duplicate actions
      R.sortBy((a: MainAction) => mainNextActions.indexOf(a)), // Sort based on the order in mainNextActions
    )

  // No CHECK after BET (FOLD and CALL are also after BET)
  return ['ANY', 'FOLD', 'CALL', 'BET']
}

type Sequence = {
  betActions: BetAction[]
  selected?: SelectedActions
  showBetActions: boolean
  id: number
  mainActions: MainAction[]
}

const createSequence = (
  index = 0,
  prevSelected: ActionSequence,
  selected: SelectedActions = [],
  showBetActions = false,
): Sequence => ({
  id: index,
  mainActions:
    index === 0
      ? mainFirstActions
      : R.pipe(
          prevSelected,
          R.take(index),
          R.filter((s: SelectedActions) => s.length > 0),
          R.map((s: SelectedActions) => s[0]),
          getNextActions,
        ),
  betActions,
  selected,
  showBetActions,
})

const parseActionSequence = (actionsSequence: ActionSequence): Sequence[] => {
  const res = actionsSequence.map((seq, i) =>
    createSequence(
      i,
      actionsSequence,
      seq,
      betActions.some(a => seq.includes(a)),
    ),
  )
  return [
    ...res,
    R.last(res)?.selected?.length ? createSequence(res.length, actionsSequence) : null,
  ].filter(Boolean) as Sequence[]
}

const sequence = ref(parseActionSequence(props.actionsSequence))

const selectedSequence = computed(
  () =>
    sequence.value
      .map(s =>
        betActions.some(a => s.selected?.includes(a))
          ? s.selected?.filter(a => a !== 'BET')
          : s.selected,
      )
      .filter(s => s?.length) as ActionSequence,
)

const reCreateNextSequence = (index: number) => {
  const currSelected = sequence.value[index]?.selected || []
  const baseSequence = R.take(sequence.value, index + 1)
  sequence.value = [
    ...baseSequence,
    currSelected.length ? createSequence(index + 1, selectedSequence.value) : null,
  ].filter(Boolean) as Sequence[]
}

const handleActionClick = (action: MainAction, sequence_index: number) => {
  const selected = sequence.value[sequence_index]?.selected || []
  if (selected.includes(action)) {
    sequence.value[sequence_index].selected = selected.filter(
      a => a !== action && !betActions.includes(a as BetAction),
    )
  } else {
    sequence.value[sequence_index].selected = [action]
  }
  reCreateNextSequence(sequence_index)
}
const handleBetActionClick = (action: BetAction, sequence_index: number) => {
  const selected = (sequence.value[sequence_index]?.selected || []).filter(
    a => !mainNextActions.includes(a as MainAction),
  )
  if (selected.includes(action)) {
    sequence.value[sequence_index].selected = selected.filter(a => a !== action)
  } else {
    sequence.value[sequence_index].selected = [...selected, action]
  }
}

const handleAnyBetActionClick = (sequence_index: number) => {
  const selected = sequence.value[sequence_index]?.selected || []
  if (betActions?.every(a => selected.includes(a))) {
    sequence.value[sequence_index].selected = ['BET']
  } else {
    sequence.value[sequence_index].selected = [...betActions, 'BET']
  }
  reCreateNextSequence(sequence_index)
}

const handleSequenceTrim = (index: number) => {
  sequence.value = R.take(sequence.value, index + 1)
  sequence.value[index].selected = []
}

const handleUpdateDebounced = R.debounce(
  (newVal: ActionSequence) => {
    emit('update:value', newVal.length ? newVal : [[]])
  },
  { waitMs: CALL_DEBOUNCE_MS },
)

const blockSelectedSequenceUpdate = ref(false)

watch(
  () => selectedSequence.value,
  newVal => {
    if (blockSelectedSequenceUpdate.value) {
      blockSelectedSequenceUpdate.value = false
      return
    }
    handleUpdateDebounced.call(newVal)
  },
  { deep: true },
)

watch(
  () => props.actionsSequence,
  newVal => {
    sequence.value = parseActionSequence(newVal)
    blockSelectedSequenceUpdate.value = true
  },
)
</script>

<template>
  <ActionSequenceLine
    v-if="variant === 'single'"
    hasTitle
    :sequence="selectedSequence"
    class="mb-2 pl-2 pt-2"
    @trim:sequence="handleSequenceTrim"
  />
  <portal
    v-else-if="variant === 'split'"
    :key="selectedSequence.map(s => s.join('-')).join('-')"
    :to="`actions_sequence_line_${street}`"
  >
    <ActionSequenceLine
      :sequence="selectedSequence"
      class="mb-2"
      @trim:sequence="handleSequenceTrim"
    />
  </portal>
  <div
    class="d-flex flex-column action_sequence_container"
    :class="variant === 'single' ? 'ga-2 pa-3' : 'ga-4'"
  >
    <GwSurface
      v-for="(actions, seq_index) in sequence"
      :key="actions.id"
      variant="surface_2"
      style="padding: var(--app-gap-2); border-radius: var(--app-rad-4)"
      class="d-flex flex-column w-100"
    >
      <div
        class="d-flex w-100"
        style="gap: var(--app-gap-2)"
      >
        <GwBtn
          v-for="action in actions.mainActions"
          :key="action"
          variant="btn_grey_white_primary"
          :active="
            actions.selected?.includes(action) ||
            (action === 'BET' && actions.selected?.some(a => betActions.includes(a as BetAction)))
          "
          style="height: var(--app-height-8)"
          class="flex-1 f-center"
          @click="handleActionClick(action, seq_index)"
        >
          <span
            class="w-100 text-center"
            v-html="
              t(
                `hhv3.filter.actions_sequence.${displayAsRaise(selectedSequence, seq_index) && action === 'BET' ? 'RAISE' : action}`,
              )
            "
          />
          <GwBtn
            v-if="action === 'BET' && !displayAsRaise(selectedSequence, seq_index)"
            variant="btn_text_grey_white_icon_grey_primary-v2"
            icon="mdi-dots-vertical"
            class="ml-auto"
            @click.stop="actions.showBetActions = !actions.showBetActions"
          ></GwBtn>
        </GwBtn>
      </div>
      <div
        v-if="actions.showBetActions"
        class="d-flex w-100 mt-2 anim-fade-in_slower"
        style="gap: var(--app-gap-2)"
      >
        <GwBtn
          :active="betActions?.every(a => actions.selected?.includes(a))"
          variant="btn_grey_white_primary"
          style="height: var(--app-height-8)"
          class="flex-1 f-center"
          @click="handleAnyBetActionClick(seq_index)"
        >
          <span
            class="w-100 text-center"
            v-html="
              t('hhv3.filter.actions_sequence.B_ANY', {
                bet_type: displayAsRaise(selectedSequence, seq_index) ? 'R' : 'B',
              })
            "
          />
        </GwBtn>
        <GwBtn
          v-for="betAction in actions.betActions"
          :key="betAction"
          :active="actions.selected?.includes(betAction)"
          variant="btn_grey_white_primary"
          style="height: var(--app-height-8)"
          class="flex-1 f-center"
          @click="handleBetActionClick(betAction, seq_index)"
        >
          <span
            class="w-100 text-center"
            v-html="
              t(`hhv3.filter.actions_sequence.B_${betAction}`, {
                bet_type: displayAsRaise(selectedSequence, seq_index) ? 'R' : 'B',
              })
            "
          />
        </GwBtn>
      </div>
    </GwSurface>
  </div>
</template>

<style scoped lang="scss">
.action_sequence_container {
  background: var(--clr-black-0-3);
  min-width: 40rem;
  border-radius: var(--app-rad-4);
}
@media (max-width: 560px) {
  .action_sequence_container {
    min-width: 100%;
  }
}
</style>
